/**
 * useDeviceDetect - Comprehensive device detection hook
 * 
 * Provides device information for responsive/adaptive UI decisions.
 * Can be used standalone or through DeviceContext for app-wide access.
 * 
 * Usage:
 *   const { isIOS, isMobile, screenSize } = useDeviceDetect();
 */

import { useState, useEffect, useCallback } from 'react';

/**
 * Detects if the device is running iOS
 */
const detectIOS = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  
  const ua = navigator.userAgent;

  const w = /** @type {Window & { MSStream?: unknown }} */ (window);

  // Check for iOS devices (MSStream excluded IE11 “IE on iOS” false positive)
  const isIOSDevice = /iPad|iPhone|iPod/.test(ua) && !w.MSStream;
  
  // Also check for iPad on iOS 13+ (reports as Mac)
  const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  
  return isIOSDevice || isIPadOS;
};

/**
 * Detects if the device is running Android
 */
const detectAndroid = () => {
  if (typeof navigator === 'undefined') return false;
  return /Android/.test(navigator.userAgent);
};

/**
 * Detects if the device is a mobile device
 */
const detectMobile = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  
  const ua = navigator.userAgent;
  
  // Check user agent for mobile indicators
  const mobileUA = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  
  // Also check screen width as fallback
  const narrowScreen = window.innerWidth <= 768;
  
  // Check for touch capability
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  return mobileUA || (narrowScreen && hasTouch);
};

/**
 * Detects if the device is a tablet
 */
const detectTablet = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  
  const ua = navigator.userAgent;
  
  // iPad detection
  const isIPad = /iPad/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  
  // Android tablet (has Android but not Mobile)
  const isAndroidTablet = /Android/.test(ua) && !/Mobile/.test(ua);
  
  // Screen size check for tablets
  const isTabletSize = window.innerWidth >= 600 && window.innerWidth <= 1024;
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  return isIPad || isAndroidTablet || (isTabletSize && hasTouch);
};

/**
 * Gets the current screen size category
 */
const getScreenSize = () => {
  if (typeof window === 'undefined') return 'md';
  
  const width = window.innerWidth;
  
  if (width < 600) return 'xs';      // Extra small (phones)
  if (width < 900) return 'sm';      // Small (large phones, small tablets)
  if (width < 1200) return 'md';     // Medium (tablets, small laptops)
  if (width < 1536) return 'lg';     // Large (desktops)
  return 'xl';                        // Extra large (large desktops)
};

/**
 * Gets the current orientation
 */
const getOrientation = () => {
  if (typeof window === 'undefined') return 'portrait';
  
  // Use screen.orientation if available
  if (window.screen?.orientation?.type) {
    return window.screen.orientation.type.includes('portrait') ? 'portrait' : 'landscape';
  }
  
  // Fallback to comparing dimensions
  return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
};

/**
 * Main device detection hook
 * 
 * @returns {Object} Device information
 */
export function useDeviceDetect() {
  const [deviceInfo, setDeviceInfo] = useState(() => ({
    isIOS: false,
    isAndroid: false,
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    isTouchDevice: false,
    screenSize: 'md',
    orientation: 'portrait',
    // Convenience getters
    isPhone: false,           // Mobile but not tablet
    isSmallScreen: false,     // xs or sm
    isLargeScreen: false,     // lg or xl
  }));

  const updateDeviceInfo = useCallback(() => {
    const isIOS = detectIOS();
    const isAndroid = detectAndroid();
    const isMobile = detectMobile();
    const isTablet = detectTablet();
    const screenSize = getScreenSize();
    const orientation = getOrientation();
    const isTouchDevice = typeof window !== 'undefined' && 
      ('ontouchstart' in window || navigator.maxTouchPoints > 0);
    
    setDeviceInfo({
      isIOS,
      isAndroid,
      isMobile,
      isTablet,
      isDesktop: !isMobile && !isTablet,
      isTouchDevice,
      screenSize,
      orientation,
      // Convenience getters
      isPhone: isMobile && !isTablet,
      isSmallScreen: screenSize === 'xs' || screenSize === 'sm',
      isLargeScreen: screenSize === 'lg' || screenSize === 'xl',
    });
  }, []);

  useEffect(() => {
    // Initial detection
    updateDeviceInfo();

    // Listen for resize and orientation changes
    const handleResize = () => {
      updateDeviceInfo();
    };

    const handleOrientationChange = () => {
      // Small delay to ensure dimensions are updated
      setTimeout(updateDeviceInfo, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [updateDeviceInfo]);

  return deviceInfo;
}

/**
 * Simple iOS detection (non-hook version for one-time checks)
 */
export const isIOSDevice = () => detectIOS();

/**
 * Simple mobile detection (non-hook version)
 */
export const isMobileDevice = () => detectMobile();

export default useDeviceDetect;

